import { Modal, Note, Paragraph, PrimaryButton, SecondaryButton } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import { type Webcast, useGetWebcastDuplicateInfoQuery } from '../../../../generated/graphql-manager';
import { getWebcastTitle } from '../../../hooks/use-get-webcast-name';

import style from './duplicate-webcast.module.css';

type Props = {
  webcastId: Webcast['id'];
  webcastName: Webcast['name'];
  onClose: () => void;
  onDuplicate: () => void;
};

export function DuplicateWebcastModal({ webcastId, webcastName, onClose, onDuplicate }: Props) {
  const { t } = useTranslation();

  const { data } = useGetWebcastDuplicateInfoQuery({ variables: { webcastId } });
  const webcast = data?.webcast;
  const primaryLanguage = webcast?.primaryLang?.code;
  const contents = webcast?.contents;

  const webcastTitle = getWebcastTitle(primaryLanguage, contents);

  return (
    <Modal
      data-testid="duplicate-webcast-modal"
      title={t('manager.webcasts.duplicateWebcast.modal.title')}
      isOpen={true}
      onClose={onClose}
      footer={
        <>
          <SecondaryButton data-testid="cancel-duplicate-webcast-button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </SecondaryButton>

          <PrimaryButton data-testid="confirm-duplicate-webcast-button" type="button" onClick={onDuplicate}>
            {t('manager.webcasts.duplicateWebcast.modal.createButton')}
          </PrimaryButton>
        </>
      }
    >
      <div className={style.wrapper}>
        <Paragraph className={style.info}>{t('manager.webcasts.duplicateWebcast.modal.info')}</Paragraph>

        <div className={style.contentWrapper}>
          <Paragraph className={style.label}>{t('manager.webcasts.duplicateWebcast.modal.eventNameLabel')}</Paragraph>
          <Paragraph
            data-testid="event-name"
            className={style.value}
          >{`${webcastName} ${t('manager.webcasts.duplicateWebcast.copy')}`}</Paragraph>
        </div>

        <div className={style.contentWrapper}>
          <Paragraph className={style.label}>{t('manager.webcasts.duplicateWebcast.modal.eventTitleLabel')}</Paragraph>
          <Paragraph data-testid="event-title" className={style.value}>
            {webcastTitle}
          </Paragraph>
        </div>

        <Note variant="primary" style={{ whiteSpace: 'pre-line', maxWidth: '100%' }}>
          {t('manager.webcasts.duplicateWebcast.modal.note')}
        </Note>
      </div>
    </Modal>
  );
}
